import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Brain, Wrench, FileText } from 'lucide-react';
export function WhatsIncluded() {
  const phases = [{
    title: 'Evaluar el perfil mental del deportista',
    desc: 'Detectá fortalezas, áreas de mejora, diferencias entre entrenamiento y competencia, respuesta al error, presión, confianza y punto de partida del proceso.'
  }, {
    title: 'Planificar el trabajo mental',
    desc: 'Convertí la evaluación inicial en objetivos claros, prioridades de intervención, indicadores de avance y un plan adaptado al deportista.'
  }, {
    title: 'Conducir sesiones individuales',
    desc: 'Aplicá un protocolo para sesiones orientadas al rendimiento: apertura, revisión, foco, herramienta, aplicación deportiva, tarea y cierre.'
  }, {
    title: 'Intervenir con equipos',
    desc: 'Trabajá comunicación, liderazgo, cohesión, roles, conflictos y dinámica interna sin caer en charlas motivacionales genéricas.'
  }, {
    title: 'Acompañar lesiones y regreso competitivo',
    desc: 'Ordená el proceso mental desde el diagnóstico hasta la vuelta a la competencia: miedo a recaer, confianza corporal y retorno progresivo.'
  }, {
    title: 'Preparar competencia y reportar avances',
    desc: 'Diseñá rutinas precompetitivas, trabajá presión, activación mental y comunicá avances al cuerpo técnico sin violar confidencialidad.'
  }];
  const tools = ['plantillas de evaluación;', 'guías de preguntas;', 'protocolos de sesión;', 'ejercicios prácticos;', 'tareas entre sesiones;', 'checklists;', 'reportes profesionales;', 'guiones de comunicación.'];
  return <section className="bg-slate-50 py-20 lg:py-28" data-id="element-175">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-176">
        <div className="mx-auto max-w-4xl text-center mb-16" data-id="element-177">
          <h2 className="mb-6 text-3xl text-slate-900 md:text-4xl lg:text-5xl font-display" data-id="element-178">
            QUÉ INCLUYE EL{' '}
            <span className="text-emerald-600" data-id="element-179">KIT PSICÓLOGO DEPORTIVO</span>
          </h2>
        </div>

        <div className="mx-auto max-w-5xl space-y-12" data-id="element-180">
          {/* Method */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="rounded-3xl bg-slate-900 p-8 shadow-lg md:p-10 text-white" data-id="element-181">
            <div className="flex items-center gap-4 mb-8" data-id="element-182">
              <Brain className="h-10 w-10 text-emerald-400" data-id="element-183" />
              <h3 className="text-2xl font-bold uppercase" data-id="element-184">
                Método Mente y Rendimiento™
              </h3>
            </div>
            <p className="text-xl text-slate-300 mb-8 font-medium" data-id="element-185">
              Una estructura profesional en 6 fases:
            </p>

            <div className="grid gap-6 md:grid-cols-2" data-id="element-186">
              {phases.map((phase, idx) => <div key={idx} className="flex gap-4 bg-slate-800/50 p-6 rounded-2xl border border-slate-700" data-id="element-187">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 font-bold" data-id="element-188">
                    {idx + 1}
                  </div>
                  <div data-id="element-189">
                    <h4 className="font-bold text-lg mb-2 text-white" data-id="element-190">
                      {phase.title}
                    </h4>
                    <p className="text-slate-400 text-sm leading-relaxed" data-id="element-191">
                      {phase.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2" data-id="element-192">
            {/* Resources */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="rounded-3xl bg-white p-8 shadow-lg border border-slate-100" data-id="element-193">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 text-blue-600" data-id="element-194">
                <Wrench className="h-7 w-7" data-id="element-195" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 uppercase" data-id="element-196">
                Recursos de Intervención Deportiva
              </h3>
              <p className="text-slate-600" data-id="element-197">
                Para momentos donde el deportista se bloquea, se frustra, pierde
                confianza, se desconcentra o no sabe cómo responder bajo
                presión.
              </p>
            </motion.div>

            {/* Tools */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="rounded-3xl bg-white p-8 shadow-lg border border-slate-100" data-id="element-198">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600" data-id="element-199">
                <FileText className="h-7 w-7" data-id="element-200" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 uppercase" data-id="element-201">
                Herramientas Profesionales Aplicables
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-202">
                Para organizar cada caso, conducir sesiones con continuidad y
                trabajar con más claridad desde la próxima consulta. Incluye:
              </p>
              <ul className="space-y-3" data-id="element-203">
                {tools.map((tool, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-204">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500 mt-0.5" data-id="element-205" />
                    <span className="text-slate-700 font-medium" data-id="element-206">{tool}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}